import {
  Alert,
  Button,
  Icon,
  IconButton,
  IconProps,
  Paper,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';

export interface ManageMembershipItemInfo {
  type: 'info';
  title: string | JSX.Element;
  subtitle?: any;
  className: string;
  rightIconName?: any;
  rightIconOnClick?: () => void;
  leftIconName?: any;
  leftIconOnClick?: () => void;
  key: string;
  mask?: boolean;
}

export interface ManageMembershipItemButton {
  type: 'button';
  text: string;
  onClick: () => void;
  iconName: any;
  className: string;
  key: string;
}

export interface ManageMembershipItemAlert {
  type: 'alert';
  severity: 'success' | 'error' | 'info' | 'warning';
  text: string | JSX.Element;
  displayCloseButton?: boolean;
  iconName: any;
  className: string;
  key: string;
}

export interface IManageMembershipItemProps {
  iconName: IconProps['iconName'];
  sectionTitle: string;
  titleClassName: string;
  items: (ManageMembershipItemInfo | ManageMembershipItemButton | ManageMembershipItemAlert)[] | [];
}

export const ManageMembershipItem = ({ iconName, sectionTitle, items, titleClassName }: IManageMembershipItemProps) => {
  const manageMembershipItems = items.map((item, i) => {
    switch (item.type) {
      case 'info':
        return (
          <Paper variant="elevation" elevation={8} sx={{ padding: '1rem' }} key={item.key}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems="center" className="member-cards">
              <Stack direction="column">
                <Typography
                  variant="b1Medium"
                  className={`${item.className}-item-title`}
                  data-qm-block={item?.mask ? 'true' : 'false'}
                >
                  {item.title}
                </Typography>
                {typeof item.subtitle === 'string' ? (
                  <Typography variant="b3Regular">{item.subtitle}</Typography>
                ) : (
                  item.subtitle
                )}
              </Stack>
              <Stack direction="row">
                {item.leftIconName && (
                  <IconButton
                    aria-label={`edit-${item.className}`}
                    onClick={item.leftIconOnClick}
                    className={`left-icon-button-${item.className}`}
                  >
                    <Icon
                      iconName={item.leftIconName}
                      sx={{ color: '#1C4DA6', fontSize: '1.5rem', padding: '0.5rem', boxSizing: 'content-box' }}
                    />
                  </IconButton>
                )}
                {item.rightIconName && (
                  <IconButton
                    aria-label={`edit-${item.className}`}
                    onClick={item.rightIconOnClick}
                    className={`right-icon-button-${item.className}`}
                  >
                    <Icon
                      iconName={item.rightIconName}
                      sx={{ color: '#1C4DA6', fontSize: '1.5rem', padding: '0.5rem', boxSizing: 'content-box' }}
                    />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </Paper>
        );
      case 'button':
        return (
          <Button
            variant="outlined"
            dashed
            fullWidth
            startIcon={<Icon iconName={item.iconName} />}
            label={item.text}
            onClick={item.onClick}
            className={`manage-membership-button-${item.className}`}
            key={`manage-membership-key-${item.key}`}
          />
        );
      case 'alert':
        return (
          <Alert
            key={item.key}
            severity={item.severity}
            displayCloseButton={item.displayCloseButton ?? false}
            icon={<Icon iconName={item.iconName} />}
            className={item.className}
          >
            {item.text}
          </Alert>
        );
      default:
        return <></>;
    }
  });

  return (
    <Stack gap="1rem" className={`${titleClassName}-section`} key={`${titleClassName}-key`}>
      <Stack direction="row" alignItems="center" gap="0.5rem">
        <Icon iconName={iconName as any} color="secondary" sx={{ fontSize: '1.5rem' }} />
        <Typography variant="h6" sx={{ fontWeight: 500 }} className={`manage-membership-${titleClassName}-title`}>
          {sectionTitle}
        </Typography>
      </Stack>
      {manageMembershipItems}
    </Stack>
  );
};
